const KEY = "cz_embedded";
let cached: boolean | null = null;

// La CZ está embebida si el tenant la abre con ?embedded=1 (contrato explícito,
// recomendado) o, como respaldo, si detecta que corre dentro de un iframe.
export function isEmbedded(): boolean {
    if (cached !== null) {
        return cached;
    }

    let embedded = false;
    try {
        const params = new URLSearchParams(window.location.search);
        if (params.get("embedded") === "1") {
            embedded = true;
        } else if (sessionStorage.getItem(KEY) === "1") {
            embedded = true;
        } else if (window.self !== window.top) {
            embedded = true;
        }
    } catch (e) {
        // Acceder a window.top entre orígenes distintos lanza => estamos en un iframe.
        embedded = true;
    }

    cached = embedded;
    if (embedded) {
        try {
            sessionStorage.setItem(KEY, "1");
        } catch (e) {
            // sessionStorage puede no estar disponible; el cache en memoria basta.
        }
        document.documentElement.classList.add("cz-embedded");
    }
    return embedded;
}

// Mensajes al host (los recibe public/embed.js). El host valida origin + source,
// así que aquí basta con targetOrigin "*" (altura/estado no son datos sensibles).
function postToParent(message: any) {
    if (!isEmbedded()) return;
    try {
        if (window.parent && window.parent !== window) {
            window.parent.postMessage(message, "*");
        }
    } catch (e) {
        // No romper la app si el host no acepta el mensaje.
    }
}

export function notifyResize(height: number) {
    if (height > 0) {
        postToParent({ type: "mk:resize", height: Math.ceil(height) });
    }
}

export function notifyScrollTop() {
    postToParent({ type: "mk:scrollTop" });
}

export function notifyPayment(status: "ok" | "ko", id?: any) {
    postToParent({ type: "mk:payment", status: status, id: id || null });
}

// El host (su menú) puede navegar el iframe: postMessage { type: "mk:navigate", path }.
// Solo se aceptan mensajes del parent y rutas internas (que empiezan por "/").
export function listenHostNavigation(cb: (path: string) => void) {
    window.addEventListener(
        "message",
        (event: MessageEvent) => {
            const data: any = event.data;
            if (!data || typeof data !== "object" || data.type !== "mk:navigate") {
                return;
            }
            try {
                if (event.source !== window.parent) {
                    return;
                }
            } catch (e) {
                return;
            }
            if (typeof data.path !== "string" || data.path.charAt(0) !== "/") {
                return;
            }
            cb(data.path);
        },
        false
    );
}

// --- Redsys embebido --------------------------------------------------------
// Cuando la CZ va en iframe, el pago no puede cargarse dentro del iframe (el banco
// bloquea el 3DS con X-Frame-Options). Se abre en un popup top-level y, al volver,
// el popup avisa al iframe que lo abrió y se cierra.

export const REDSYS_POPUP_NAME = "mkRedsysPopup";

// Abre el popup de pago. DEBE llamarse dentro del gesto del click (antes de cualquier
// await) o el navegador lo bloquea. Devuelve la ventana, o null si no aplica/falla.
export function openRedsysPopup(): Window | null {
    if (!isEmbedded()) return null;
    try {
        const w = 480, h = 720;
        const left = window.screenX + Math.max(0, (window.outerWidth - w) / 2);
        const top = window.screenY + Math.max(0, (window.outerHeight - h) / 2);
        return window.open(
            "",
            REDSYS_POPUP_NAME,
            `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`
        );
    } catch (e) {
        return null;
    }
}

// ¿Esta página es EL popup de Redsys que abrimos nosotros?
// Clave: comprobar window.name === REDSYS_POPUP_NAME (lo fija window.open al abrirlo).
// Mirar solo window.opener es PELIGROSO: una pestaña normal abierta con target="_blank"
// o window.open también tiene opener => al volver a /cz/ok ejecutaría window.close() y
// CERRARÍA LA PESTAÑA DEL USUARIO. El name solo lo tiene nuestro popup.
export function isRedsysPopup(): boolean {
    try {
        return window.name === REDSYS_POPUP_NAME && !!window.opener && window.opener !== window;
    } catch (e) {
        return false;
    }
}

// Desde el popup (en /cz/ok|/cz/ko): avisa al iframe que lo abrió y cierra el popup.
export function notifyOpenerPaymentAndClose(status: "ok" | "ko", id?: any) {
    try {
        if (window.opener && window.opener !== window) {
            window.opener.postMessage(
                { type: "mk:redsys-return", status: status, id: id || null },
                window.location.origin
            );
        }
    } catch (e) {
        // no romper
    }
    try {
        window.close();
    } catch (e) {
        // el navegador puede impedir cerrar; el iframe ya recibió el mensaje
    }
}

// En el iframe: escucha el retorno del popup de Redsys (mismo origin que el iframe).
export function listenRedsysReturn(cb: (status: "ok" | "ko", id: any) => void) {
    window.addEventListener(
        "message",
        (event: MessageEvent) => {
            const data: any = event.data;
            if (!data || typeof data !== "object" || data.type !== "mk:redsys-return") {
                return;
            }
            if (event.origin !== window.location.origin) {
                return;
            }
            cb(data.status === "ok" ? "ok" : "ko", data.id);
        },
        false
    );
}
